import { useCallback, useEffect, useRef, useState } from 'react';

import { setAIVoiceoverBatchComplete } from 'js/src/data/ai-voices-and-avatars/ai-voices-and-avatars-actions';
import { batchConvertTextToVoiceover } from 'js/src/data/ai-voices-and-avatars/ai-voices-and-avatars-utils';
import { UNKNOWN_LANGUAGE_CODE } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

export const useGenerateVoiceoverQueue = (
    maxWordCount: number,
    languageCode: string | null = UNKNOWN_LANGUAGE_CODE,
) => {
    // We do not want to pre-generate voiceovers past the max word count
    const wordCount = useRef<number>(0);
    const [initialBatchConverted, setInitialBatchConverted] = useState(false);
    const [triggerTimeout, setTriggerTimeout] = useState(false);
    const timeoutId = useRef<number | null>(null);
    const dispatch = useAppDispatch();

    // This state represents the queue of voiceovers to generate. It is a list of
    // objects with the text to generate and the voice preset to use.
    const [queue, setQueue] = useState<{ nodeKey: string; text: string; voicePreset: string }[]>(
        [],
    );

    const processQueue = useCallback(async () => {
        if (!queue || !queue?.length || initialBatchConverted) {
            return;
        }
        setInitialBatchConverted(true);
        const sentences = [...new Set(queue.map((item) => item.text))];
        dispatch(setAIVoiceoverBatchComplete(false));
        const regenerate = false;
        if (languageCode) {
            const useTransientBucket = true;
            await batchConvertTextToVoiceover(
                sentences,
                queue[0].voicePreset,
                regenerate,
                languageCode,
                undefined,
                useTransientBucket,
            );
        }
        dispatch(setAIVoiceoverBatchComplete(true));
        setQueue([]);
    }, [queue, initialBatchConverted, dispatch, languageCode]);

    useEffect(() => {
        if (!triggerTimeout || !languageCode) {
            return;
        }
        if (timeoutId.current) {
            window.clearTimeout(timeoutId.current);
        }
        timeoutId.current = window.setTimeout(() => {
            processQueue();
        }, 100);
        setTriggerTimeout(false);
    }, [triggerTimeout, processQueue, languageCode]);

    const enqueue = useCallback(
        (nodeKey: string, text: string, voicePreset: string | null) => {
            if (!voicePreset) {
                return;
            }
            if (wordCount.current < maxWordCount) {
                setQueue((prevQueue) => [...prevQueue, { nodeKey, text, voicePreset }]);
                wordCount.current += text.trim().split(' ').length;
                setTriggerTimeout(true);
            }
        },
        [wordCount, maxWordCount],
    );

    const remove = useCallback((nodeKey: string) => {
        setQueue((prevQueue) => prevQueue.filter((item) => item.nodeKey !== nodeKey));
        setTriggerTimeout(true);
    }, []);

    const size = queue.length;

    return {
        enqueue,
        remove,
        size,
    };
};

/**
 * Hook for handling audio previews for AI voiceover.
 * It handles events that affect audio element playback as well as state changes that react to them.
 */
export function useBlockAudioPreview() {
    const audioPreviewElementRef = useRef<HTMLAudioElement>(new Audio());
    const [currentlyPlayingBlock, setCurrentlyPlayingBlock] = useState<string | null>(null);

    useEffect(() => {
        const audioElement = audioPreviewElementRef.current;
        const handlePlay = () => {
            setCurrentlyPlayingBlock(audioElement.getAttribute('nodeKey'));
        };
        const handlePause = () => {
            setCurrentlyPlayingBlock(null);
        };
        audioElement.addEventListener('play', handlePlay);
        audioElement.addEventListener('pause', handlePause);
        audioElement.addEventListener('ended', handlePause);
        // Ensure that playback does not continue when component is unmounted
        return () => {
            audioElement.pause();
            audioElement.removeEventListener('play', handlePlay);
            audioElement.removeEventListener('pause', handlePause);
            audioElement.removeEventListener('ended', handlePause);
        };
    }, []);

    const setAudioPreviewElementSrc = useCallback((newSrc: string) => {
        audioPreviewElementRef.current.src = newSrc;
    }, []);

    return {
        audioPreviewElementRef,
        currentlyPlayingBlock,
        setAudioPreviewElementSrc,
    };
}
